import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import "./ActivatePage.css";

const ActivatePage = () => {
  const { uid, token } = useParams();
  const [message, setMessage] = useState("Activating your account...");
  const [isLoading, setIsLoading] = useState(true);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const activateAccount = async () => {
      try {
        const response = await axios.post(
          `${SERVER_URL}/api/auth/users/activation/`,
          { uid, token }
        );

        if (isMounted) {
          setIsLoading(false);
          setMessage("Account activated successfully! Redirecting...");
          setTimeout(() => navigate("/signin"), 3000);
        }
      } catch (err) {
        const errorDetail = err.response?.data?.detail || "Unknown error";

        if (isMounted) {
          setIsLoading(false);
          if (errorDetail === "Stale token for given user.") {
            setMessage("Account is successfully activated! Redirecting...");
            setTimeout(() => navigate("/signin"), 3000);
          } else {
            setMessage(`Activation failed: ${errorDetail}`);
          }
        }
      }
    };

    activateAccount();

    return () => {
      isMounted = false;
    };
  }, [uid, token, navigate, SERVER_URL]);

  return (
    <div className="activate-container">
      <Helmet>
        <title>Activate Your Account – LingoLab</title>
        <meta
          name="description"
          content="Activate your LingoLab account to access AI-powered video translation and localization services. Secure, fast, and easy account activation."
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://lingolab.ca/activate" />
        <meta property="og:title" content="Activate Your Account – LingoLab" />
        <meta
          property="og:description"
          content="Activate your LingoLab account and start translating videos with advanced AI technology."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lingolab.ca/activate" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta name="twitter:title" content="Activate Your Account – LingoLab" />
        <meta
          name="twitter:description"
          content="Activate your LingoLab account and start translating videos with advanced AI technology."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <div className="activate-card" role="alert" aria-live="polite">
        <h2 className="activate-title">Account Activation</h2>
        <div className="activate-message">
          {isLoading ? (
            <div className="activate-loading">
              <FaSpinner className="animate-spin" />
              <span>{message}</span>
            </div>
          ) : (
            <p>{message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivatePage;
